import { supabase } from '@/lib/supabase';
import { clearAuthHeadersCache, primeAuthHeaders } from '@/lib/orchestration-api';
import type { AuthUser, UserRole } from '../stores/auth-store';
import { normalizePhone, safeParsePhone } from '@/lib/phone';

export interface LoginCredentials {
  username: string;
  password: string;
  role?: 'admin' | 'rider';
}

export interface LoginResponse {
  success: boolean;
  user?: AuthUser;
  message?: string;
  token?: string;
}

async function getAdminProfileForUser(user: { id: string; email?: string | null }) {
  if (!supabase) {
    return null;
  }

  if (user.email) {
    const emailResult = await supabase
      .from('admin_users')
      .select('*')
      .eq('email', user.email)
      .maybeSingle();

    if (!emailResult.error) {
      return emailResult.data;
    }

    console.warn('[Login API] Failed to load admin profile by email:', emailResult.error.message);
  }

  const authUserIdResult = await supabase
    .from('admin_users')
    .select('*')
    .eq('auth_user_id', user.id)
    .maybeSingle();

  if (authUserIdResult.error) {
    const authUserIdLookupFailed =
      authUserIdResult.error.message.includes('auth_user_id') ||
      authUserIdResult.error.code === 'PGRST204' ||
      authUserIdResult.error.code === '42703';

    if (!authUserIdLookupFailed) {
      console.warn('[Login API] Failed to load admin profile by auth user id:', authUserIdResult.error.message);
    }

    return null;
  }

  return authUserIdResult.data;
}

/**
 * Admin/Staff login via Supabase Auth or Edge Functions
 */
export async function login(credentials: LoginCredentials): Promise<LoginResponse> {
  console.log('[Login API] Starting login process for username:', credentials.username);
  console.log('[Login API] Role:', credentials.role || 'admin (default)');
  
  if (!supabase) {
    console.error('[Login API] Supabase not configured - cannot authenticate');
    return {
      success: false,
      message: 'Authentication service not configured. Please contact support.',
    };
  }

  const isRider = credentials.role === 'rider';

  // Rider login uses Edge Function (PIN-based)
  if (isRider) {
    return riderLoginViaEdge(credentials.username, credentials.password);
  }

  // Admin login via Supabase Auth
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: credentials.username,
      password: credentials.password,
    });

    if (error) {
      console.error('[Login API] Supabase Auth error:', error.message);
      return {
        success: false,
        message: error.message === 'Invalid login credentials'
          ? 'Invalid email or password. Please check your credentials.'
          : error.message,
      };
    }

    if (!data.user) {
      return {
        success: false,
        message: 'Login succeeded but no user data was returned.',
      };
    }

    const ensuredSession = data.session ?? (await supabase.auth.getSession()).data.session;
    const accessToken = ensuredSession?.access_token?.trim();

    if (!accessToken) {
      return {
        success: false,
        message: 'Login succeeded, but no valid session token was available. Please try again.',
      };
    }

    clearAuthHeadersCache();
    primeAuthHeaders(accessToken);

    // Fetch admin user profile
    const adminProfile = await getAdminProfileForUser(data.user);

    const role = (adminProfile?.role || 'admin').replace('_', '-') as UserRole;
    
    const user: AuthUser = {
      id: data.user.id,
      email: data.user.email || credentials.username,
      username: data.user.email || credentials.username,
      name: adminProfile?.name || data.user.user_metadata?.name || data.user.email || '',
      role,
      phone: adminProfile?.phone,
      branchId: adminProfile?.branch_id,
      avatar: data.user.user_metadata?.avatar,
    };

    console.log('[Login API] User mapped:', { id: user.id, role: user.role, name: user.name });

    return {
      success: true,
      user,
      token: accessToken,
    };
  } catch (error) {
    console.error('[Login API] EXCEPTION during login:');
    console.error('[Login API] Error:', error instanceof Error ? error.message : String(error));
    return {
      success: false,
      message: 'Network error. Please check your connection and try again.',
    };
  }
}

/**
 * Customer login/registration via phone
 * Creates or retrieves a customer profile
 */
export async function customerLogin(phone: string): Promise<LoginResponse> {
  try {
    // Use centralized phone utility for consistent normalization
    const normalized = normalizePhone(phone);
    if (!normalized.success) {
      return {
        success: false,
        message: 'Invalid phone number format. Please enter a valid Kenyan mobile number.',
      };
    }
    const formattedPhone = normalized.data;

    // Check if customer profile exists
    const { data: existingProfile, error: profileError } = await supabase
      .from('customer_profiles')
      .select('*')
      .eq('phone', formattedPhone)
      .single();

    if (profileError && profileError.code !== 'PGRST116') {
      throw profileError;
    }

    let profile = existingProfile;

    // Create new profile if doesn't exist
    if (!profile) {
      const { data: newProfile, error: createError } = await supabase
        .from('customer_profiles')
        .insert({
          phone: formattedPhone,
          created_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (createError) throw createError;
      profile = newProfile;
    }

    const user: AuthUser = {
      id: profile.id,
      email: '',
      username: formattedPhone,
      name: profile.name || formattedPhone,
      role: 'customer',
      phone: formattedPhone,
    };

    return {
      success: true,
      user,
    };
  } catch (error) {
    console.error('Customer login error:', error);
    return {
      success: false,
      message: 'Failed to login. Please try again.',
    };
  }
}

/**
 * Rider login via phone + PIN
 * Validates rider credentials and returns rider data
 */
export interface RiderLoginCredentials {
  phone: string;
  pin: string;
}

export interface RiderLoginResponse {
  ok: boolean;
  rider?: {
    id: string;
    name: string;
    phone: string;
    branch_id: string;
    branch?: any;
  };
  error?: string;
}

async function riderLoginViaEdge(phone: string, pin: string): Promise<LoginResponse> {
  try {
    const { data, error } = await supabase!.functions.invoke('rider-login', {
      body: { phone, pin },
    });

    if (error || !data?.ok) {
      return {
        success: false,
        message: data?.error || 'Invalid phone or PIN',
      };
    }

    const rider = data.rider;
    const user: AuthUser = {
      id: rider.id,
      email: '',
      username: rider.phone,
      name: rider.name,
      role: 'rider',
      phone: rider.phone,
      branchId: rider.branch_id,
    };

    return {
      success: true,
      user,
      token: data.token,
    };
  } catch (error) {
    console.error('Rider login error:', error);
    return {
      success: false,
      message: 'An unexpected error occurred. Please try again.',
    };
  }
}

export async function riderLogin(credentials: RiderLoginCredentials): Promise<RiderLoginResponse> {
  try {
    const { data, error } = await supabase!.functions.invoke('rider-login', {
      body: credentials,
    });

    if (error || !data?.ok) {
      return {
        ok: false,
        error: data?.error || 'Invalid phone or PIN',
      };
    }

    return {
      ok: true,
      rider: data.rider,
    };
  } catch (error) {
    console.error('Rider login error:', error);
    return {
      ok: false,
      error: 'An unexpected error occurred. Please try again.',
    };
  }
}

/**
 * Logout using Supabase Auth
 */
export async function logout(): Promise<void> {
  if (supabase) {
    await supabase.auth.signOut();
  }
  clearAuthHeadersCache();
}

/**
 * Get current session
 */
export async function getCurrentSession() {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session;
}

/**
 * Listen to auth state changes
 */
export function onAuthStateChange(callback: (event: string, session: any) => void) {
  if (!supabase) return { data: { subscription: { unsubscribe: () => {} } } };
  return supabase.auth.onAuthStateChange(callback);
}
